import React from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Checkbox, Radio, RadioCard } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';
import getBillingModel, { BILLING_MODELS } from '@dashboard/services/getBillingModel';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import Description from '@dashboard/components/Description';
import {
  Item,
  ItemAction,
  ItemContent,
  ItemDescription,
  ItemLink,
  ItemTitle,
} from '@dashboard/components/ui/item';
import useStripePaymentMethodConfigurations from '@dashboard/hooks/useStripePaymentMethodConfigurations';
import Divider from '@dashboard/components/Divider';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

function getPaymentMethod(paymentMethods) {
  // if card and agpay are enabled, return 'card_agpay'
  if (paymentMethods.card?.enabled && paymentMethods.agpay?.enabled) {
    return 'card_agpay';
  }

  // if card is enabled, return 'card'
  if (paymentMethods.card?.enabled) {
    return 'card';
  }

  // if muliple is enabled, return 'multiple'
  if (paymentMethods.multiple?.enabled) {
    return 'multiple';
  }
}

function parsePaymentMethods(paymentMethod, mode) {
  switch (paymentMethod) {
    case 'card_agpay':
      return {
        card: { enabled: true },
        agpay: { enabled: true, mode },
        multiple: { enabled: false },
      };
    case 'card':
      return {
        card: { enabled: true },
        agpay: { enabled: false, mode },
        multiple: { enabled: false },
      };
    case 'multiple':
      return {
        card: { enabled: false },
        agpay: { enabled: false, mode },
        multiple: { enabled: true },
      };
    default:
      return {};
  }
}

const PaymentMethodsForm = () => {
  const { seller, isAdminLoggedInAsSeller } = useAuth();
  const { page, updatePage } = usePage();
  const { data: pmcs, isLoading: pmcsLoading } = useStripePaymentMethodConfigurations({
    sellerId: seller._id,
    enabled: true,
  });
  const defaultPmc = pmcs?.find(pmc => pmc.is_default);
  // Assumes the standard Connect platform layout: one Default PMC (one-time
  // payments) and one separate PMC for subscription invoices. Picks the first
  // non-default PMC as the subscription one.
  const billingPmc = pmcs?.find(pmc => !pmc.is_default);
  const hasAnyPmc = Boolean(defaultPmc || billingPmc);

  // The one-time link always shows (subscriptions bill setup fees / one-time
  // variant components through the default PMC); the subscription link shows only
  // when the page has a recurring price (subscription | mixed) — derived from the
  // prices, so a pure-charge page hides it without needing the billingModel migration.
  const billingModel = getBillingModel(page.product);
  const showSubscriptionMethods = billingModel !== BILLING_MODELS.CHARGE;
  const isMixedBilling = billingModel === BILLING_MODELS.MIXED;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        paymentMethod: getPaymentMethod(page.paymentMethods.stripe),
        mode: page.paymentMethods.stripe.agpay?.mode || 'default',
        savePaymentMethod: page.savePaymentMethod || false,
      }}
      onSubmit={async (values, actions) => {
        if (values.paymentMethod === 'multiple') {
          if (!checkIsConnectedToStripe(seller)) {
            actions.setStatus('First connect to Stripe to enable multiple payment methods');
            return;
          }
        }

        actions.setStatus(undefined);

        const paymentMethods = parsePaymentMethods(values.paymentMethod, values.mode);

        const { data, error } = await updatePage.mutateAsync({
          paymentMethods: {
            stripe: paymentMethods,
          },
          savePaymentMethod: values.savePaymentMethod,
        });

        if (data) {
          actions.setStatus('');
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ status, dirty, handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <UnsavedChangesReporter />
          <FormElement>
            <Label>Payment methods</Label>
            <RadioCard.Group name="paymentMethod" columns={1}>
              <RadioCard.Card name="paymentMethod" value="card_agpay">
                <RadioCard.Label>Card + Apple / Google Pay</RadioCard.Label>
                <RadioCard.LabelDescription>
                  Accept card payments along with Apple Pay and Google Pay
                </RadioCard.LabelDescription>
              </RadioCard.Card>
              <RadioCard.Card name="paymentMethod" value="card">
                <RadioCard.Label>Card only</RadioCard.Label>
                <RadioCard.LabelDescription>Accept card payments only</RadioCard.LabelDescription>
              </RadioCard.Card>
              <CanUseFeature feature={Features.MULTIPLE_PAYMENT_METHODS}>
                <RadioCard.Card name="paymentMethod" value="multiple">
                  <RadioCard.Label>Multiple payment methods</RadioCard.Label>
                  <RadioCard.LabelDescription>
                    Accept cards, wallets, and buy now, pay later options
                  </RadioCard.LabelDescription>
                </RadioCard.Card>
              </CanUseFeature>
            </RadioCard.Group>

            <FormError name="paymentMethod" />
          </FormElement>

          {values.paymentMethod === 'multiple' && (
            <FormElement>
              <div className="mt-3 flex flex-col gap-2">
                <Label className="!mb-0">Enable payment methods in Stripe</Label>
                {pmcsLoading && (
                  <>
                    <StripeConfigRow
                      title="Enable one-time payment methods"
                      subtitle="Used for one-time payments"
                      loading
                    />
                    {showSubscriptionMethods && (
                      <StripeConfigRow
                        title="Enable subscription payment methods"
                        subtitle="Used for recurring payments"
                        loading
                      />
                    )}
                  </>
                )}

                {!pmcsLoading && hasAnyPmc && (
                  <>
                    {defaultPmc && (
                      <StripeConfigRow
                        href={getStripeDashboardUrl(`/settings/payment_methods/${defaultPmc.id}`, {
                          seller,
                          isAdminLoggedInAsSeller,
                        })}
                        title="Enable one-time payment methods"
                        subtitle="Used for one-time payments"
                      />
                    )}
                    {showSubscriptionMethods && billingPmc && (
                      <StripeConfigRow
                        href={getStripeDashboardUrl(`/settings/payment_methods/${billingPmc.id}`, {
                          seller,
                          isAdminLoggedInAsSeller,
                        })}
                        title="Enable subscription payment methods"
                        subtitle="Used for recurring payments"
                      />
                    )}
                  </>
                )}

                {!pmcsLoading && !hasAnyPmc && (
                  <StripeConfigRow
                    href={getStripeDashboardUrl(
                      `/settings/payment_methods?platform_id=${process.env.REACT_APP_STRIPE_PLATFORM_ID}`,
                      { seller, isAdminLoggedInAsSeller },
                    )}
                    title="Open payment methods in Stripe"
                    subtitle="Choose which methods to accept on your account"
                  />
                )}

                {isMixedBilling && (
                  <p className="text-xs text-muted-foreground">
                    This checkout sells one-time and subscription prices — on the checkout, the
                    available payment methods update to match the price the customer selects.
                  </p>
                )}
              </div>
            </FormElement>
          )}

          {values.paymentMethod === 'card_agpay' && (
            <FormElement>
              <Label htmlFor="mode">Apple Pay / Google Pay behavior</Label>

              <RadioCard.Group name="mode" columns={1}>
                <RadioCard.Card value="default" name="mode" id="default">
                  <RadioCard.Label>Payment method</RadioCard.Label>
                  <RadioCard.LabelDescription>
                    Apple Pay / Google Pay will be offered as payment method alongside card at
                    checkout.
                  </RadioCard.LabelDescription>
                </RadioCard.Card>

                <RadioCard.Card value="express" name="mode" id="express">
                  <RadioCard.Label>Express checkout</RadioCard.Label>
                  <RadioCard.LabelDescription>
                    Automatically collects name and email, but allows your customer to skip other
                    checkout fields.
                  </RadioCard.LabelDescription>
                </RadioCard.Card>
              </RadioCard.Group>

              <FormError name="mode" />
            </FormElement>
          )}

          {(values.paymentMethod === 'card_agpay' || values.paymentMethod === 'multiple') && (
            <Alert variant="default" className="my-4">
              <InformationCircleIcon />
              <AlertTitle>Note</AlertTitle>
              <AlertDescription>
                <p>
                  To use Apple Pay / Google Pay on embed and pop-up checkouts on your site, you need
                  to{' '}
                  <Link href="/settings/payments#payment-method-domains">register your domain</Link>
                </p>
              </AlertDescription>
            </Alert>
          )}

          <Divider className="mb-4" />

          <FormElement>
            <Checkbox.Wrapper>
              <Checkbox name="savePaymentMethod" id="savePaymentMethod" />
              <Checkbox.Label
                htmlFor="savePaymentMethod"
                tooltip="Automatically saves the customer's payment method to your Stripe account when they make a one-time payment. This lets you charge them again in the future if needed. Payment methods for subscriptions are always saved."
              >
                Save payment method for one-time payments
              </Checkbox.Label>
            </Checkbox.Wrapper>
          </FormElement>

          {status && <FormErrorBase>{status}</FormErrorBase>}

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

function StripeConfigRow({ href, title, subtitle, loading }) {
  const content = (
    <>
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        <ItemDescription>{subtitle}</ItemDescription>
      </ItemContent>
      <ItemAction>
        <ArrowTopRightOnSquareIcon className="transition-transform duration-150 group-hover:-translate-y-px group-hover:translate-x-px" />
      </ItemAction>
    </>
  );

  if (loading || !href) {
    return (
      <Item
        aria-busy={loading || undefined}
        className="border border-neutral-200 dark:border-border px-4 py-3 opacity-50"
      >
        {content}
      </Item>
    );
  }

  return (
    <ItemLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group border border-neutral-200 dark:border-border px-4 py-3"
    >
      {content}
    </ItemLink>
  );
}

export default PaymentMethodsForm;
